'use client'

import { useEffect, useState, useRef, useMemo } from 'react'


import ZoneOption from './ZoneOption'
import { mxData } from '../utils/constants'
import { useAutocompleteStore } from '../store'
import { ZoneCoincidence } from '../interfaces/zoneInterface'
import { getZoneCoincidences } from '../services/zoneServices'
import { GoogleMapsStructuredPlace } from '../interfaces/googleMapsInterfaces'
import { ESearchLevel } from '../enums'

let timeout: NodeJS.Timeout
let isTyping = false

interface Props {
  hidden?: boolean
  searchText: string
  itemClassName?: string
  itemTextClassName?: string
  selectedIndex: number
  focusOptionClassNames?: string
  withRemarkSelected?: boolean
  remarkSelectedClassNames?: string
  showResults: boolean
  onSelected: (place: GoogleMapsStructuredPlace) => void
  setTotalZones: (totalZones: number) => void
  onLoadedZoneCoincidencesService: (isReady: boolean) => void
}

const ZoneCoincidences = (props: Props): JSX.Element => {
  const {
    searchText,
    hidden,
    itemClassName,
    itemTextClassName,
    selectedIndex,
    focusOptionClassNames,
    showResults,
    withRemarkSelected,
    remarkSelectedClassNames,
    onSelected,
    setTotalZones,
    onLoadedZoneCoincidencesService
  } = props

  const { setTotalSuggestionsZones } = useAutocompleteStore()

  const [coincidences, setCoincidences] = useState<ZoneCoincidence[]>([])

  const searchTextRef = useRef(searchText)
  searchTextRef.current = searchText

  const loadCoincidences = async (params: { label: string }): Promise<void> => {
    onLoadedZoneCoincidencesService(false)
    const { label } = params

    const { isSuccess, data } = await getZoneCoincidences({ text: label })

    if (isTyping) {
      return
    }

    if (!isSuccess || !data?.length) {
      setCoincidences([])
      setTotalZones(0)
      setTotalSuggestionsZones([])
      onLoadedZoneCoincidencesService(true)

      return
    }

    const zoneNames = data.map((zone) => zone.properties.zoneName)

    setCoincidences(data)
    setTotalSuggestionsZones(zoneNames)
    setTotalZones(data.length)
    onLoadedZoneCoincidencesService(true)
  }

  const handleKeyDown = async (event: any): Promise<void> => {
    if (!showResults) {
      return
    }

    if (
      event.key === 'Enter' &&
      selectedIndex >= 0 &&
      coincidences &&
      selectedIndex < coincidences.length
    ) {
      const {
        properties: { zoneName, zoneId, latitude, longitude, stateName }
      } = coincidences[selectedIndex]

      const select: GoogleMapsStructuredPlace = {
        fullAddress: `${zoneName},  ${stateName}`,
        zoneId,
        searchLevel: ESearchLevel.NEIGHBORHOOD,
        coordinates: {
          latitude,
          longitude
        },
        navigation: {
          state: mxData.name,
          municipality: stateName,
          neighborhood: zoneName,
          property: ''
        },
        placeId: '',
        streetNumber: '',
        street: '',
        neighborhood: '',
        town: '',
        location: '',
        postalCode: ''
      }

      onSelected(select)
    }
  }

  const isSomeSelected = useMemo(() => {
    const textInInput = searchText?.replace(/ /g, '').toLowerCase()
    return coincidences.some((coincidence) => {
      const coincidenceName = `${coincidence?.properties?.zoneName}, ${coincidence?.properties?.stateName}`
      const textInSuggestion = coincidenceName?.replace(/ /g, '').toLowerCase()
      return textInInput === textInSuggestion
    })
  }, [searchText, coincidences])

  useEffect(() => {
    if (coincidences.length > 0) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [coincidences, selectedIndex])

  useEffect(() => {
    if (searchText.length < 1) {
      setCoincidences([])
      setTotalZones(0)
      setTotalSuggestionsZones([])
      return
    }

    isTyping = true

    clearTimeout(timeout)

    timeout = setTimeout(() => {
      isTyping = false
      if (
        searchText &&
        searchTextRef.current === searchText &&
        !isSomeSelected
      ) {
        loadCoincidences({ label: searchText })
      }
    }, 300)

    if (!searchText) {
      setCoincidences([])
      setTotalZones(0)
      setTotalSuggestionsZones([])
    }
  }, [searchText, isSomeSelected])

  return (
    <>
      {coincidences.length && showResults ? (
        <div hidden={hidden}>
          <p className="text-xs text-[#898CA8]">Zonas</p>

          <div className="flex flex-col mt-2.5">
            {coincidences.map((coincidence, index) => {
              return (
                <ZoneOption
                  searchText={searchText}
                  index={index}
                  selectedIndex={selectedIndex}
                  withRemarkSelected={withRemarkSelected}
                  key={coincidence?.properties?.zoneId}
                  coincidence={coincidence}
                  remarkSelectedClassNames={remarkSelectedClassNames}
                  itemTextClassName={itemTextClassName}
                  onSelected={onSelected}
                  className={itemClassName}
                  focusOptionClassNames={focusOptionClassNames}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default ZoneCoincidences
